import { Injectable } from '@angular/core';
import { HasAccessService } from '@client/app/pipe/has-access/has-access.service';
import { MON_MODULES } from '@monsido/core/constants/mon-modules.constant';
import { SessionService } from '@monsido/core/session/session.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { EarlyAccessService } from '@monsido/services/early-access/early-access.service';
import { JIRA_CONNECTOR_CONSTANT } from '@monsido/core/constants/jira-connector.constant';
import { PAGE_CORRECT_DOMAIN_SETTINGS_MODULE } from '@client/ng2/modules/page-fix/constants/domain-settings-modules.constant';
import {
    ADMIN_SIDE_NAV_ITEMS,
    DOMAIN_SIDE_NAV_ITEMS,
    SideNavigationItem,
    SideNavigationProfile,
    SideNavigationVisibilityKey,
} from '@client/app/feature-modules/navigation/components/side-navigation/side-navigation.menu-config';

export interface SideNavigationMenuModel {
    profile: SideNavigationProfile;
    items: SideNavigationItem[];
}

interface SideNavigationVisibilityContext {
    redesignedAccessibilityEnabled: boolean;
    hasLegacyPdfClarityAccess: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class SideNavigationMenuResolverService {
    constructor (
        private readonly sessionService: SessionService,
        private readonly activeFeatureService: ActiveFeatureService,
        private readonly hasAccessService: HasAccessService,
        private readonly earlyAccessService: EarlyAccessService,
    ) {}

    resolve (stateName?: string): SideNavigationMenuModel {
        const profile = this.resolveProfileForState(stateName);
        const visibilityContext = this.createVisibilityContext();

        const sourceItems = profile === 'admin'
            ? ADMIN_SIDE_NAV_ITEMS
            : DOMAIN_SIDE_NAV_ITEMS;

        const visibleItems = this.compileVisibleItems(sourceItems, visibilityContext);
        const adjustedItems = this.applyDynamicAccessibilityRoutes(visibleItems, visibilityContext);
        const normalizedItems = this.normalizeRootDividers(adjustedItems);

        return {
            profile,
            items: normalizedItems,
        };
    }

    private resolveProfileForState (stateName?: string): SideNavigationProfile {
        if (this.isAdminState(stateName)) {
            return 'admin';
        }

        return 'domains';
    }

    private isAdminState (stateName?: string): boolean {
        return Boolean(stateName?.startsWith('base.customer.admin'));
    }

    private createVisibilityContext (): SideNavigationVisibilityContext {
        const redesignedAccessibilityEnabled = this.isRedesignedAccessibilityEnabled();

        return {
            redesignedAccessibilityEnabled,
            hasLegacyPdfClarityAccess: !redesignedAccessibilityEnabled && this.hasLegacyPdfClarityAccess(),
        };
    }

    private compileVisibleItems (
        items: SideNavigationItem[],
        visibilityContext: SideNavigationVisibilityContext,
    ): SideNavigationItem[] {
        const visibleItems: SideNavigationItem[] = [];

        for (const item of items) {
            if (item.visibilityKey && !this.isVisibilityEnabled(item.visibilityKey, visibilityContext)) {
                continue;
            }

            const visibleItem: SideNavigationItem = {
                ...item,
            };

            let visibleChildren: SideNavigationItem[] | undefined;
            if (item.children && item.children.length > 0) {
                visibleChildren = this.compileVisibleItems(item.children, visibilityContext);
                if (visibleChildren.length === 0) {
                    continue;
                }
            }

            if (visibleChildren && visibleChildren.length > 0) {
                visibleItem.children = visibleChildren;
            }

            visibleItems.push(visibleItem);
        }

        return visibleItems;
    }

    private applyDynamicAccessibilityRoutes (
        items: SideNavigationItem[],
        visibilityContext: SideNavigationVisibilityContext,
    ): SideNavigationItem[] {
        if (!visibilityContext.hasLegacyPdfClarityAccess) {
            return items;
        }

        return this.replaceRouteForItem(
            items,
            'accessibility-pdfs-internal',
            'base.customer.domain.accessibility.internal-pdf-accessibility',
        );
    }

    private replaceRouteForItem (
        items: SideNavigationItem[],
        itemId: string,
        route: string,
    ): SideNavigationItem[] {
        return items.map((item) => {
            const nextItem: SideNavigationItem = item.id === itemId
                ? {
                    ...item,
                    route,
                }
                : {
                    ...item,
                };

            if (item.children && item.children.length > 0) {
                nextItem.children = this.replaceRouteForItem(item.children, itemId, route);
            }

            return nextItem;
        });
    }

    private normalizeRootDividers (items: SideNavigationItem[]): SideNavigationItem[] {
        return items.map((item, index) => ({
            ...item,
            dividerAfter: Boolean(item.dividerAfter && index < items.length - 1),
        }));
    }

    private isVisibilityEnabled (
        visibilityKey: SideNavigationVisibilityKey,
        visibilityContext: SideNavigationVisibilityContext,
    ): boolean {
        switch (visibilityKey) {
            case 'prioritized-content':
                return this.hasCustomerPlanTrait('prioritized_content_all') && this.hasModuleAccess(MON_MODULES.content_pages);
            case 'accessibility-root':
                return Boolean(this.sessionService.domain?.features?.accessibility) &&
                    this.hasModuleAccess(MON_MODULES.accessibility);
            case 'accessibility-modern':
                return visibilityContext.redesignedAccessibilityEnabled;
            case 'accessibility-legacy':
                return !visibilityContext.redesignedAccessibilityEnabled;
            case 'accessibility-pdfs':
                return this.isAccessibilityPdfsEnabled();
            case 'accessibility-pdfs-modern':
                return visibilityContext.redesignedAccessibilityEnabled && this.isAccessibilityPdfsEnabled();
            case 'accessibility-pdfs-legacy':
                return !visibilityContext.redesignedAccessibilityEnabled && this.isAccessibilityPdfsEnabled();
            case 'quality-root':
                return this.hasQualityRootAccess();
            case 'quality-summary':
                // Summary and Issues by Page only show if at least one core QA feature is enabled
                return this.hasModuleAccess(MON_MODULES.qa) &&
                    (
                        this.hasCustomerPlanTrait('qa_spellcheck') ||
                        this.hasCustomerPlanTrait('qa_links') ||
                        this.hasCustomerPlanTrait('readability')
                    );
            case 'quality-policies':
                return this.hasPolicyNavigationAccess();
            case 'quality-pagecorrect':
                return this.hasPageCorrectNavigationAccess();
            case 'quality-pagecorrect-accessibility-fixes':
                return this.hasPageCorrectAccessibilityFixesAccess();
            case 'quality-spellcheck':
                return this.activeFeatureService.isFeatureActivePure('qa_spellcheck');
            case 'quality-links':
                return this.activeFeatureService.isFeatureActivePure('qa_links');
            case 'quality-readability':
                return this.sessionService.domain?.features?.readability_test !== null &&
                    this.activeFeatureService.isFeatureActivePure('admin_readability');
            case 'quality-language':
                return this.activeFeatureService.isFeatureActivePure('language_match');
            case 'insights-root':
                return this.allowStatistics() ||
                    this.hasCustomerPlanTrait('heartbeat') ||
                    this.activeFeatureService.isFeatureActivePure('history_center_all') ||
                    this.hasCustomerPlanTrait('inventory_all');
            case 'site-performance-root':
                return this.hasCustomerPlanTrait('seo_all') ||
                    (this.activeFeatureService.isFeatureActive('page_performance') && this.hasModuleAccess(MON_MODULES.performance)) ||
                    (this.hasCustomerPlanTrait('heartbeat') && this.hasModuleAccess(MON_MODULES.uptime));
            case 'privacy-root':
                return this.hasDataPrivacyCoreAccess() || this.hasConsentManagerAccess();
            case 'statistics-root':
                return this.allowStatistics() && this.hasModuleAccess(MON_MODULES.statistics);
            case 'uptime-root':
                return this.hasCustomerPlanTrait('heartbeat') && this.hasModuleAccess(MON_MODULES.uptime);
            case 'history-center':
                return this.activeFeatureService.isFeatureActivePure('history_center_all');
            case 'inventory-root':
                return this.hasCustomerPlanTrait('inventory_all') && this.hasModuleAccess(MON_MODULES.inventory);
            case 'seo-root':
                return this.hasCustomerPlanTrait('seo_all') && this.hasModuleAccess(MON_MODULES.seo);
            case 'performance-root':
                return this.activeFeatureService.isFeatureActive('page_performance') && this.hasModuleAccess(MON_MODULES.performance);
            case 'data-privacy-core':
                return this.hasDataPrivacyCoreAccess();
            case 'consent-manager-root':
                return this.hasConsentManagerAccess();
            case 'consent-manager-cookie-overview':
                return this.sessionService.hasRole('customer_admin');
            case 'admin-labels':
                return this.activeFeatureService.isFeatureActive('labels');
            case 'admin-audit-log':
                return this.activeFeatureService.isFeatureActive('audit_log');
            case 'admin-source-code-excludes':
                return this.hasCustomerPlanTrait('source_code_excludes') &&
                    this.activeFeatureService.isFeatureActive('admin_source_code_excludes');
            case 'admin-api-users':
                return this.activeFeatureService.isFeatureActive('api_users');
            case 'admin-page-performance':
                return this.activeFeatureService.isFeatureActive('admin_page_performance');
            case 'admin-jira-connector':
                return this.activeFeatureService.isFeatureActive(JIRA_CONNECTOR_CONSTANT.PLAN_TRAIT);
            default:
                return true;
        }
    }

    private hasCustomerPlanTrait (trait: string): boolean {
        const customer = this.sessionService.customer;
        if (!customer) {
            return false;
        }

        const planTraits = customer.plan_traits as unknown as Record<string, unknown>;
        return Boolean(planTraits[trait]);
    }

    private hasModuleAccess (module: string): boolean {
        return this.hasAccessService.hasAccess(this.sessionService.me || undefined, module);
    }

    private hasQualityRootAccess (): boolean {
        // Core QA features (spellcheck, links, readability) show the full Content Quality section
        const hasQualityAccess = this.hasModuleAccess(MON_MODULES.qa) &&
            (
                this.hasCustomerPlanTrait('qa_spellcheck') ||
                this.hasCustomerPlanTrait('qa_links') ||
                this.hasCustomerPlanTrait('readability')
            );

        // Language Validation also enables Content Quality root (but NOT Summary/Issues by Page)
        const hasLanguageAccess = this.activeFeatureService.isFeatureActivePure('language_match');

        return hasQualityAccess || hasLanguageAccess || this.hasPolicyNavigationAccess() || this.hasPageCorrectNavigationAccess();
    }

    private hasPolicyNavigationAccess (): boolean {
        return this.hasModuleAccess(MON_MODULES.policies) &&
            this.activeFeatureService.isFeatureActivePure('global_policy') &&
            this.activeFeatureService.isFeatureActivePure('policies_all');
    }

    private hasPageCorrectNavigationAccess (): boolean {
        return this.activeFeatureService.isFeatureActive(MON_MODULES.page_fix) &&
            this.hasModuleAccess(MON_MODULES.pagecorrect);
    }

    private hasPageCorrectAccessibilityFixesAccess (): boolean {
        if (!this.hasPageCorrectNavigationAccess() || !this.activeFeatureService.isFeatureActive(MON_MODULES.accessibility)) {
            return false;
        }

        const checks = this.sessionService.domain?.settings?.scripts?.page_correct?.enabled_checks;
        return Array.isArray(checks) && checks.includes(PAGE_CORRECT_DOMAIN_SETTINGS_MODULE.ACCESSIBILITY_CHECK);
    }

    private hasDataPrivacyCoreAccess (): boolean {
        const domain = this.sessionService.domain || undefined;
        return this.activeFeatureService.isFeatureActive(MON_MODULES.data_privacy, domain) &&
            this.hasModuleAccess(MON_MODULES.data_privacy);
    }

    private hasConsentManagerAccess (): boolean {
        const hasConsentManagerPlanTrait = this.hasCustomerPlanTrait('cookie_banner');
        const isDemoPlan = this.sessionService.customer?.plan_type === 'demo';
        const hasCookieBannerFeature = Boolean(this.sessionService.domain?.features?.cookie_banner);

        return hasConsentManagerPlanTrait && (isDemoPlan || hasCookieBannerFeature);
    }

    private isAccessibilityPdfsEnabled (): boolean {
        return Boolean(this.sessionService.domain?.scan?.scan_documents);
    }

    private isRedesignedAccessibilityEnabled (): boolean {
        try {
            return this.earlyAccessService.isFeatureEnabled('redesignAccessibility');
        } catch {
            return false;
        }
    }

    private hasLegacyPdfClarityAccess (): boolean {
        const customerHasClarityAccess = this.hasCustomerPlanTrait('clarity_pdf_accessibility');
        const hasDemoClarityAccess = this.sessionService.customer?.plan_type === 'demo' &&
            Boolean(this.sessionService.domain?.scan?.scan_documents) &&
            Boolean(this.sessionService.domain?.features?.accessibility) &&
            !customerHasClarityAccess;

        return customerHasClarityAccess || hasDemoClarityAccess;
    }

    private allowStatistics (): boolean {
        if (this.sessionService.customer?.plan_type === 'demo') {
            return true;
        }

        if (!this.hasCustomerPlanTrait('statistics')) {
            return false;
        }

        if (this.sessionService.domain && this.sessionService.domainGroup === null) {
            return Boolean(this.sessionService.domain.features?.statistics);
        }

        if (this.sessionService.domainGroup !== null) {
            return Boolean(this.sessionService.domain?.features?.statistics) &&
                this.hasCustomerPlanTrait('domain_group_statistics');
        }

        return false;
    }
}
